import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Typography, Grid, IconButton,
  Alert, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Divider, Tabs, Tab
} from '@mui/material';
import {
  ArrowBack as BackIcon, Edit as EditIcon, ContentCopy as DuplicateIcon,
  CheckCircle as ActivateIcon, Cancel as DeactivateIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi, { MillingRecipe, RecipeStatus } from '../../services/millingApi';

const statusColors: Record<RecipeStatus, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  active: 'success',
  inactive: 'warning',
  obsolete: 'error',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MillingRecipeDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [recipe, setRecipe] = useState<MillingRecipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const data = await millingApi.getRecipe(id!);
      setRecipe(data);
    } catch (err) {
      console.error(err);
      setError('Error loading recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      await millingApi.activateRecipe(id!, 'admin');
      fetchRecipe();
    } catch (err) {
      alert('Error activating recipe');
    }
  };

  const handleDeactivate = async () => {
    try {
      await millingApi.deactivateRecipe(id!, 'admin');
      fetchRecipe();
    } catch (err) {
      alert('Error deactivating recipe');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error || !recipe) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Recipe not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/milling/recipes')}>Back to List</Button>
      </Box>
    );
  }

  const totalStandardCost = (recipe.standardLaborCost || 0) + (recipe.standardOverheadCost || 0) +
    (recipe.standardDepreciationCost || 0) + (recipe.standardUtilitiesCost || 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/milling/recipes')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">{recipe.recipeCode}</Typography>
            <Typography variant="subtitle1" color="text.secondary">{recipe.recipeName}</Typography>
          </Box>
          <Chip label={recipe.status} color={statusColors[recipe.status]} sx={{ ml: 2 }} />
          {recipe.isDefault && <Chip label="Default" color="primary" variant="outlined" sx={{ ml: 1 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {recipe.status === 'draft' && (
            <Button startIcon={<ActivateIcon />} color="success" variant="outlined" onClick={handleActivate}>
              Activate
            </Button>
          )}
          {recipe.status === 'active' && (
            <Button startIcon={<DeactivateIcon />} color="warning" variant="outlined" onClick={handleDeactivate}>
              Deactivate
            </Button>
          )}
          <Button startIcon={<DuplicateIcon />} variant="outlined">Duplicate</Button>
          <Button startIcon={<EditIcon />} variant="contained" onClick={() => navigate(`/milling/recipes/${id}/edit`)}>
            Edit
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Inputs" />
        <Tab label="Outputs" />
        <Tab label="Costs" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Primary Input</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item Code</Typography>
                    <Typography>{recipe.inputItemCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item Name</Typography>
                    <Typography>{recipe.inputItemName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Standard Quantity</Typography>
                    <Typography fontWeight="bold">{recipe.standardInputQty} {recipe.inputUom}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Primary Output</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item Code</Typography>
                    <Typography>{recipe.outputItemCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item Name</Typography>
                    <Typography>{recipe.outputItemName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Standard Quantity</Typography>
                    <Typography fontWeight="bold">{recipe.standardOutputQty} {recipe.outputUom}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Expected Yield</Typography>
                    <Typography fontWeight="bold" color="success.main">{recipe.expectedYieldPercent}%</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Yield Parameters</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Expected Yield</Typography>
                    <Typography fontWeight="bold">{recipe.expectedYieldPercent}%</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Min Yield</Typography>
                    <Typography>{recipe.minYieldPercent || 0}%</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Max Yield</Typography>
                    <Typography>{recipe.maxYieldPercent || 100}%</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Processing Time</Typography>
                    <Typography>{recipe.processingTimeMinutes || 0} min</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {recipe.description && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography>{recipe.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Input Materials</Typography>
            {recipe.inputRows && recipe.inputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Standard Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="center">Optional</TableCell>
                      <TableCell>Warehouse</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipe.inputRows.map((row, idx) => (
                      <TableRow key={row.id || idx}>
                        <TableCell>{row.lineNo}</TableCell>
                        <TableCell><Typography fontWeight="bold">{row.itemCode}</Typography></TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>{row.uom}</TableCell>
                        <TableCell align="right">{row.standardQty}</TableCell>
                        <TableCell align="right">${row.unitCost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="center">{row.isOptional ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{row.warehouseName || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No additional input materials defined.</Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Output Products</Typography>
            {recipe.outputRows && recipe.outputRows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Expected Qty</TableCell>
                      <TableCell align="right">Yield %</TableCell>
                      <TableCell align="right">Cost Alloc %</TableCell>
                      <TableCell align="right">Market Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipe.outputRows.map((row, idx) => (
                      <TableRow key={row.id || idx}>
                        <TableCell>{row.lineNo}</TableCell>
                        <TableCell><Typography fontWeight="bold">{row.itemCode}</Typography></TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.outputType.replace('_', ' ')}
                            size="small"
                            color={row.isMainProduct ? 'primary' : row.isByProduct ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">{row.expectedQty} {row.uom}</TableCell>
                        <TableCell align="right">{row.expectedYieldPercent}%</TableCell>
                        <TableCell align="right">{row.costAllocationPercent}%</TableCell>
                        <TableCell align="right">${row.marketPrice?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No by-products or secondary outputs defined.</Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Standard Costs</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Labor Cost</TableCell>
                        <TableCell align="right">${recipe.standardLaborCost?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Overhead Cost</TableCell>
                        <TableCell align="right">${recipe.standardOverheadCost?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Depreciation Cost</TableCell>
                        <TableCell align="right">${recipe.standardDepreciationCost?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Utilities Cost</TableCell>
                        <TableCell align="right">${recipe.standardUtilitiesCost?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Typography fontWeight="bold">Total Standard Cost</Typography></TableCell>
                        <TableCell align="right"><Typography fontWeight="bold">${totalStandardCost.toFixed(2)}</Typography></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cost per Unit</Typography>
                <Typography variant="h3" color="primary">
                  ${recipe.standardOutputQty ? (totalStandardCost / recipe.standardOutputQty).toFixed(2) : '0.00'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  per {recipe.outputUom} of {recipe.outputItemName}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Version</Typography>
            <Typography>{recipe.version || '1.0'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Created By</Typography>
            <Typography>{recipe.createdBy}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Created At</Typography>
            <Typography>{recipe.createdAt ? new Date(recipe.createdAt).toLocaleString() : '-'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Updated At</Typography>
            <Typography>{recipe.updatedAt ? new Date(recipe.updatedAt).toLocaleString() : '-'}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MillingRecipeDetail;
